import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import StyledButton from "../../../app/components/styledComponents/StyledButton";
import FormikInputField from "../../../app/components/formikComponents/FormikInputField";
import moment from "moment";
import moments from "moment-timezone";
import FormikSelect from "../../../app/components/formikComponents/FormikSelect";
import StyledRadioButton from "../../../app/components/radio/StyledRadioButton";
import StyledFilterTabs from "../../../app/components/styledComponents/StyledFilterTabs";
import PortletHeader from "../../../app/components/portlet/PortletHeader";
import { StyledLabel, Wrap } from "../../../assets/styledComponents/Global";
import styled, { useTheme } from "styled-components";
import FormikAsyncSelect from "../../../app/components/formikComponents/FormikAsyncSelect";
import { toast } from "react-toastify";
import { useGetScheduleReportByIdQuery, useGetSupersetDashboardTabsMutation, useScheduleReportMutation, useUpdateScheduleReportMutation } from "../reportingApiSlice";
import { formatTimezone, generateCronExpression, getEmailFields, parseCronPattern } from "../reportingUtils";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "../../../app/components/loader/EllipticalLoader";
import TagInput from "../../../app/components/taginput/TagInput";

// ✅ Validation schema function
const validationSchema = () =>
  Yup.object()
    .shape({
      reportName: Yup.string().required("Report Name is required"),
      description: Yup.string().required("Description is required"),
      timeZone: Yup.string().required("Time Zone is required"),
      frequency: Yup.string().required("Frequency is required"),
      contentType: Yup.string().required("Content Type is required"),
      messageFormat: Yup.string().required("Please select a message format"),

      scheduleTime: Yup.string().when("frequency", {
        is: (val) => ["Daily", "Weekly", "Monthly"].includes(val),
        then: (schema) => schema.required("Schedule Time is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Leave these nullable to control via test
      scheduleDay: Yup.string().nullable(),
      scheduleMonthDay: Yup.string().nullable(),

      chart: Yup.object().when("contentType", {
        is: "chart",
        then: (schema) => schema.required("Chart is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      dashboard: Yup.object().when("contentType", {
        is: "dashboard",
        then: (schema) => schema.required("Dashboard is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

     
      emailAddress: Yup.string().required("Email Address is required").test("valid-emails", "Recipients are separated by commas", (value) => { if (!value) return false; const emails = value.split(",").map((e) => e.trim()); const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; return emails.every((email) => regex.test(email)); }),
    })
    .test(
      "schedule-fields-validation",
      function (values) {
        const { frequency, scheduleDay, scheduleMonthDay } = values;

        if (frequency === "Weekly" && !scheduleDay) {
          return this.createError({
            path: "scheduleDay",
            message: "Schedule Day is required for Weekly frequency",
          });
        }

        if (frequency === "Monthly" && !scheduleDay && !scheduleMonthDay) {
          return this.createError({
            path: "scheduleDay",
            message:
              "Please select either Schedule Day or Schedule Month Day for Monthly",
          });
        }

        return true;
      }
    );

const Reportingcreation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const timezone = moment.tz.guess();
  const offset = moment.tz(timezone).format("Z");
  const formattedTimeZone = `GMT${offset} (${timezone})`;
  let { id } = useParams();
  const timezonesOptions = useMemo(() => {
    const timeZones = moments.tz
      .names()
      .sort((a, b) => moment.tz(a).utcOffset() - moment.tz(b).utcOffset());
    return timeZones.map((tz) => ({
      label: `GMT${moments.tz(tz).format("Z")} (${tz})`,
      value: tz,
    }));
  }, []);
  const [scheduleAReport, { isLoading: postLoader }] = useScheduleReportMutation();
  const [updateScheduleAReport, { isLoading: loading }] = useUpdateScheduleReportMutation();
  const handleSubmit = (data) => {
    if (id) {
      updateScheduleAReport({ ...data, id: id }).unwrap().then((res) => {
        toast.success(res?.message || 'Report updated successfully');
        navigate('/schedule/reporting');
      })
    } else {
      scheduleAReport(data).unwrap().then((res) => {
        toast.success(res?.message || 'Report scheduled successfully');
        navigate('/schedule/reporting');
      })
    }

  }

  const {
    data: reportData,
    isFetching,
  } = useGetScheduleReportByIdQuery(
    {
      id
    },
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    },
  );


  const { emailAddress,cc,bcc } = id
    ? getEmailFields(reportData?.data?.recipients)
    : { emailAddress: "", cc: [], bcc: [] };

    const [tab,setTab] = useState();
  const [getSupersetDashboardTabs] = useGetSupersetDashboardTabsMutation();
  useEffect(() => {
    if(reportData?.data?.dashboard?.id){
    getSupersetDashboardTabs({ id: reportData?.data?.dashboard?.id }).unwrap().then((resp)=>{
setTab(resp);
    });
  }
  }, [reportData]);

  return (
    <>
      {isFetching && <StyledOverLay>
        <div className="d-flex justify-content-center align-items-center"> <Spinner /></div>
      </StyledOverLay>}
      <StyledRerport className={`d-flex justify-content-center align-items-start`}>
        <div className="w-100">
          <Formik
            enableReinitialize
            initialValues={{
              reportName: id ? reportData?.data?.name : "",
              description: id ? reportData?.data?.description : "",
              timeZone: id ? reportData?.data?.timezone : formattedTimeZone,
              scheduleDay: id ? parseCronPattern(reportData?.data?.crontab)?.scheduleDay || "Every Day Of Week" : "Every Day Of Week",
              scheduleMonthDay: id ? parseCronPattern(reportData?.data?.crontab)?.scheduleMonthDay || "Every Day Of Month" : "Every Day Of Month",
              dashboard: (id && reportData?.data?.dashboard) ? { value: reportData?.data?.dashboard?.id, label: reportData?.data?.dashboard?.dashboard_title } : "",
              scheduleTime: id ? parseCronPattern(reportData?.data?.crontab)?.scheduleTime : "",
              frequency: id ? parseCronPattern(reportData?.data?.crontab)?.frequency : "Daily",
              contentType: id ? reportData?.data?.dashboard ? "dashboard" : 'chart' : "",
              chart: id ? { value: reportData?.data?.chart?.id, label: reportData?.data?.chart?.slice_name } : "",
              messageFormat: id ? reportData?.data?.report_format : "PDF",
              tab:tab?.options.find(opt => opt.value === reportData?.data?.extra?.dashboard?.anchor) || '',
              email_subject:reportData?.data?.email_subject || '',
              cc:cc,
              bcc:bcc,
              emailAddress
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              let data = {
                name: values.reportName,
                description: values.description,
                timezone: formatTimezone(values.timeZone),
                email_subject: values?.email_subject,
                creation_method: "alerts_reports",
                active: true,
                extra: (values.contentType === 'dashboard' && values?.tab?.value?.length > 0) ? {dashboard: {anchor: values?.tab?.value}} : {},
                crontab: generateCronExpression(values),
                type: "Report",
                report_format: values.messageFormat,
                 chart: values.contentType === 'chart' ? values?.chart?.value : null ,
                 dashboard: values.contentType === 'dashboard' ? values?.dashboard?.value : null ,
                recipients: [
                  {
                    recipient_config_json: {
                      target: values.emailAddress,
                      ccTarget: values.cc?.length > 0 ? values.cc?.join(", ") : '', 
                      bccTarget: values.bcc?.length > 0 ? values.bcc?.join(", ") : '',
                    },
                    type: "Email"
                  }
                ]

              }
              handleSubmit(data)
            }}
          >
            {({ setFieldValue, values }) => (
              <Form className="row g-2">
                <PortletHeader
                  backLink='/schedule/reporting'
                  title={'Schedule Reports'}
                  titleDetails={'Find scheduled reports here & schedule new report'}
                  containerClassName="portlet__body"
                  actions={
                    <div
                      className={`${'d-flex w-100 justify-content-center align-items-center'

                        }`}
                    >
                      <StyledButton
                        variant='white'
                        disabled={loading || postLoader}
                        onClick={() => (window.location.href = "/schedule/reporting")}
                      >
                        Cancel
                      </StyledButton>
                      <StyledButton type='submit' variant="primary" className='ms-2' loading={loading || postLoader} disabled={loading || postLoader}>
                        Schedule This Report
                      </StyledButton>
                    </div>
                  }
                >

                </PortletHeader>
                {/* ------------------ LEFT COLUMN (8) ------------------ */}
                <div className="col-md-8 ps-3 overflow-hidden">
                  <ReportCard className="p-3 schedule-left">
                    <ReportStepStrip aria-hidden="true">
                      <span className="active">Details</span>
                      <span>Schedule</span>
                      <span>Content</span>
                      <span>Delivery</span>
                    </ReportStepStrip>
                    {/* Report Name */}
                    <div>
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Report Name</StyledLabel>
                      <FormikInputField
                        name="reportName"
                        placeholder="Enter report name"
                        className="rounded p-2 mt-1"
                      />
                    </div>

                    {/* Description */}
                    <div className="mt-3 pb-3">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Description</StyledLabel>
                      <FormikInputField
                        as="textarea"
                        name="description"
                        placeholder="Enter description"
                        className="rounded p-2 mt-1"
                      />
                    </div>
                    <ReportSectionDivider>
                      <StyledLabel size='12px' weight='600' className="mb-0 text-uppercase" color={theme?.subText || theme?.textSecondary} style={{ letterSpacing: '0.04em' }}>
                        Scheduling Options
                      </StyledLabel>
                    </ReportSectionDivider>
                    {/* Frequency Selector */}
                    <div className="d-flex justify-content-between mt-2">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">
                        Frequency
                      </StyledLabel>
                      <StyledFilterTabs padding="5px">
                        <div className="btn-group" role="group">
                          {["Daily", "Weekly", "Monthly"].map((freq) => {
                            const isActive = values.frequency === freq;
                            return (
                              <button
                                key={freq}
                                type="button"
                                className={`btn ${isActive ? "btn-secondary active" : ""}`}
                                onClick={() => setFieldValue("frequency", freq)}
                              >
                                {freq}
                              </button>
                            );
                          })}
                        </div>
                      </StyledFilterTabs>
                    </div>

                    {/* Time Zone */}
                    <div className="mt-3">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Time Zone</StyledLabel>
                      <FormikSelect
                        id="timeZone"
                        name="timeZone"
                        options={timezonesOptions}
                        onChange={(data) => setFieldValue("timeZone", data?.label)}
                        placeholder="Select time zone"
                      />
                    </div>
                    <div className="row align-items-start mt-3">
                      {values.frequency === "Monthly" && (<div className="col-md-4">
                        <StyledLabel className="ms-4" size='14px' weight='600' font="Raleway, sans-serif">Every Day Of Month</StyledLabel>
                        <div className="d-flex align-items-center mt-1">
                          {values.frequency === "Monthly" && <span className="me-2 fw-bold">on</span>}
                          <div className="flex-grow-1">
                            <FormikSelect
                              name="scheduleMonthDay"
                              placeholder="Select Day"
                              options={Array.from({ length: 31 }, (_, i) => ({
                                label: `${i + 1}`,
                                value: `${i + 1}`,
                              }))}
                            />
                          </div>
                        </div>
                      </div>)}
                      {(values.frequency === "Weekly" || values.frequency === "Monthly") && (<div className={` col-md-${values.frequency === "Weekly" ? '6' : '4'}`}>
                        <StyledLabel className={values.frequency !== "Daily" ? `ms-4` : ''} size='14px' weight='600' font="Raleway, sans-serif">Every Day Of Week</StyledLabel>
                        <div className="d-flex align-items-center mt-1">
                          {values.frequency !== "Daily" && <span className="me-2 fw-bold">{values.frequency === "Weekly" ? 'on' : 'or'}</span>}
                          <div className="flex-grow-1">
                            <FormikSelect
                              name="scheduleDay"
                              placeholder="Select Weekday"
                              options={[
                                { label: "Monday", value: "Monday" },
                                { label: "Tuesday", value: "Tuesday" },
                                { label: "Wednesday", value: "Wednesday" },
                                { label: "Thursday", value: "Thursday" },
                                { label: "Friday", value: "Friday" },
                                { label: "Saturday", value: "Saturday" },
                                { label: "Sunday", value: "Sunday" },
                              ]}
                            />
                          </div>
                        </div>
                      </div>)}
                      <div className={` col-md-${values.frequency === "Weekly" ? '6' : values.frequency === "Monthly" ? '4' : '12'}`}>
                        <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Schedule Time</StyledLabel>
                        <div className="d-flex align-items-center mt-1">
                          <div className="flex-grow-1">
                            <FormikInputField
                              name="scheduleTime"
                              type="time"
                              placeholder="Select Time"
                            />
                          </div>
                        </div>
                      </div>
                    </div>





                    {/* Content Type */}
                    <div className="mt-3">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Content Type</StyledLabel>
                      <FormikSelect
                        name="contentType"
                        placeholder="Select Content Type"
                        options={[
                          { label: "Chart", value: "chart" },
                          { label: "Dashboard", value: "dashboard" },
                        ]}
                         onChange={(option) => {
      setFieldValue("contentType", option.value);
      // Reset radio button default value to PDF
      setFieldValue("messageFormat", "PDF");
    }}
                      />
                    </div>

                    {/* Chart */}
                    {values?.contentType === 'chart' &&
                      <div className="mt-3">
                        <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Select Chart</StyledLabel>
                        <FormikAsyncSelect
                          name="chart"
                          id="chart"
                          type={'charts'}
                          params={{perPage:50, page:0,query:'~'}}
                          placeholder="Select a chart"
                          //   position="top"
                          CustomOptionLayout={(option) => (
                            <div className="d-flex align-items-center justify-content-between">
                              <Wrap className="d-flex flex-column gap-1 align-items-start">
                                <StyledLabel size={'14px'} weight={'600'}>{option?.label}</StyledLabel>
                                <Wrap className='d-flex justify-content-end align-items-baseline '>
                                  <StyledLabel size={'12px'} weight={'400'}>{option?.value}</StyledLabel>
                                </Wrap>
                              </Wrap>
                            </div>
                          )}
                        />
                      </div>}

                    {/* Dashbaord */}
                    {values?.contentType === 'dashboard' &&
                      <div className="mt-3">
                        <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Select Dashboard</StyledLabel>
                        <FormikAsyncSelect
                          name="dashboard"
                          id="dashboard"
                          type={'dashboards'}
                          params={{}}
                          placeholder="Select a dashboard"
                          //   position="top"
                          CustomOptionLayout={(option) => (
                            <div className="d-flex align-items-center justify-content-between">
                              <Wrap className="d-flex flex-column gap-1 align-items-start">
                                <StyledLabel size={'14px'} weight={'600'}>{option?.label}</StyledLabel>
                                <Wrap className='d-flex justify-content-end align-items-baseline '>
                                  <StyledLabel size={'12px'} weight={'400'}>{option?.value}</StyledLabel>
                                </Wrap>
                              </Wrap>
                            </div>
                          )}
                        />
                      </div>}



                    {/* Dashbaord Tabs*/}
                    {(values?.contentType === 'dashboard' && values?.dashboard?.value) &&
                      <div className="mt-3">
                        <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Select Tab</StyledLabel>
                        <FormikAsyncSelect
                          name="tab"
                          id="tab"
                          type={'tabs'}
                          params={{tabId:values?.dashboard?.value}}
                          placeholder="Select a tab"
                          //   position="top"
                          CustomOptionLayout={(option) => (
                            <div className="d-flex align-items-center justify-content-between">
                              <Wrap className="d-flex flex-column gap-1 align-items-start">
                                <StyledLabel size={'14px'} weight={'600'}>{option?.label}</StyledLabel>
                                <Wrap className='d-flex justify-content-end align-items-baseline '>
                                  <StyledLabel size={'12px'} weight={'400'}>{option?.value}</StyledLabel>
                                </Wrap>
                              </Wrap>
                            </div>
                          )}
                        />
                      </div>}


                    {/* Message Format */}
                    <div className="mt-3">
  <StyledLabel size="14px" weight="600" font="Raleway, sans-serif">
    Message Content Attached In Email
  </StyledLabel>

  <div className="d-flex align-items-center flex-wrap mt-2 gap-4">
    {[
      { id: 1, title: "Text embedded & PDF", value: "PDF" },
      { id: 2, title: "Image (PNG)", value: "PNG" },
      ...(values?.contentType === "chart"
        ? [{ id: 3, title: "Formatted CSV", value: "CSV" }]
        : []),
    ].map((option, index) => (
      <StyledRadioButton
        key={`radio-${index}-${option.id}`}
        label={option.title}
        size="16"
        id={`${index}-${option.id}`}
        value={values.messageFormat === option.value}
        onChange={() => setFieldValue("messageFormat", option.value)}
      />
    ))}
  </div>
</div>
                  </ReportCard>
                </div>

                {/* ------------------ RIGHT COLUMN (4) ------------------ */}
                <div className="col-md-4 pe-3">
                  <ReportCard className="p-4 h-auto">
                    <div className="d-flex align-items-center pb-4">
                      <StyledLabel size='18px' weight='400' font="Fredoka, sans-serif">
                        Share Schedule Report
                      </StyledLabel>
                    </div>

                    <div className="mb-3">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Email Address</StyledLabel>
                      <FormikInputField
                        name="emailAddress"
                        placeholder="Enter email"
                        className="rounded p-2 mt-1"
                      />
                      <StyledLabel size='14px' weight='400' font="Raleway, sans-serif" className="fst-italic mt-2" color={theme.secondary}>This report will be sent to your email address</StyledLabel>
                    </div>
                    <div className="mb-3">
                     <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">Email subject name (optional)</StyledLabel>
                    <FormikInputField
                        name="email_subject"
                        placeholder="Enter subject"
                        className="rounded p-2 mt-1"
                      />
                    </div>
                    <hr></hr>
                    <StyledEmailTags className="mb-3">
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">CC Recipients  <span className="text-muted">(optional)</span></StyledLabel>
                       <TagInput
                                            validationRegex={
                                              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
                                            }
                                            className="w-100"
                                            tagsClass={ 'customTags'}
                                            inputClass="customInputs w-100"
                                            name="cc"
                                            addOnBlur={true}
                                            value={values.cc}
                                            placeholder={'Enter cc email(s)'}
                                            onChange={(keywords) => setFieldValue('cc', keywords)}
                                          />
                      {/* <FormikInputField
                        name="cc"
                        placeholder="Enter CC email(s)"
                        className="rounded p-2 mt-1"
                      /> */}
                    </StyledEmailTags>
                    <StyledEmailTags>
                      <StyledLabel size='14px' weight='600' font="Raleway, sans-serif">BCC Recipients <span className="text-muted">(optional)</span></StyledLabel>
                     
                      <TagInput
                                            validationRegex={
                                              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
                                            }
                                            className="w-100"
                                            tagsClass={ 'customTags'}
                                            inputClass="customInputs w-100"
                                            name="bcc"
                                            addOnBlur={true}
                                            value={values.bcc}
                                            placeholder={'Enter bcc email(s)'}
                                            onChange={(keywords) => setFieldValue('bcc', keywords)}
                                          />
                                          
                      {/* <FormikInputField
                        name="bcc"
                        placeholder="Enter BCC email(s)"
                        className="rounded p-2 mt-1"
                      /> */}
                    </StyledEmailTags>

                  </ReportCard>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </StyledRerport>
    </>
  );
};

const StyledRerport = styled.div`
.schedule-left {
  height: calc(90vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;
}
`;

const sheetBorder = ({ theme }) =>
  theme?.dark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(13, 35, 73, 0.08)';

const fieldFill = ({ theme }) =>
  theme?.dark ? theme.fieldLabelBg || '#343B45' : '#fff';

const fieldLine = ({ theme }) =>
  theme?.dark ? 'rgba(255, 255, 255, 0.34)' : theme.inputBorder || '#d0d5dd';

const ReportCard = styled.div`
  background: ${({ theme }) => (theme?.dark ? theme.surface || '#28303A' : '#fff')};
  border: 1px solid ${sheetBorder};
  border-radius: ${({ theme }) => theme.radiusCard || '10px'};
  color: ${({ theme }) => theme.headingColor};

  label,
  span {
    color: ${({ theme }) => theme.headingColor};
  }

  && .form-control,
  && textarea,
  && input[type='time'] {
    background-color: ${fieldFill} !important;
    border: 1px solid ${fieldLine} !important;
    color: ${({ theme }) => theme.headingColor} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.headingColor};
    color-scheme: ${({ theme }) => (theme?.dark ? 'dark' : 'light')};
    box-shadow: none !important;
    outline: none;
  }

  && .form-control::placeholder,
  && textarea::placeholder,
  && input::placeholder {
    color: ${({ theme }) => theme.inputPlaceHolder || '#9C9C9C'} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.inputPlaceHolder || '#9C9C9C'};
    opacity: 1;
  }

  && input:-webkit-autofill {
    -webkit-text-fill-color: ${({ theme }) => theme.headingColor} !important;
    -webkit-box-shadow: 0 0 0 30px ${fieldFill} inset !important;
  }

  && [class$='-control'] {
    background: ${fieldFill} !important;
    border-color: ${fieldLine} !important;
    box-shadow: none !important;
    outline: none;
  }

  && [class$='-control'] input,
  && .react-tagsinput input {
    border: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  && [class$='-singleValue'],
  && [class$='-placeholder'],
  && [class$='-Input'] input,
  && [class$='-indicatorContainer'] {
    color: ${({ theme }) => theme.headingColor} !important;
  }

  && [class$='-indicatorContainer'] svg {
    fill: ${({ theme }) => theme.headingColor};
  }
`;

const ReportStepStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${sheetBorder};

  span {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) =>
      theme?.dark ? theme.headingColor || '#fff' : '#6f767e'} !important;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${({ theme }) =>
      theme?.dark ? theme.fieldLabelBg || '#343B45' : '#eff4fa'};
  }

  span.active {
    color: ${({ theme }) => (theme?.dark ? '#fff' : '#0d2349')} !important;
    background: ${({ theme }) =>
      theme?.dark ? theme.secondary || '#dc601e' : 'rgba(13, 35, 73, 0.1)'};
  }
`;

const ReportSectionDivider = styled.div`
  margin: 8px 0 4px;
  padding-top: 12px;
  border-top: 1px solid ${sheetBorder};
`;

const StyledOverLay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme?.dark ? 'rgba(16, 25, 36, 0.72)' : 'rgba(255, 255, 255, 0.7)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const StyledEmailTags = styled.div`
  .react-tagsinput {
    border: 1px solid ${fieldLine} !important;
    background: ${fieldFill} !important;
    border-radius: 8px;
    padding: 0px 5px;
  }
  .customInputs,
  .react-tagsinput-input {
    font-size: 14px;
    color: ${({ theme }) => theme.headingColor} !important;
  }
  .react-tagsinput-input::placeholder {
    color: ${({ theme }) => theme.inputPlaceHolder || '#9C9C9C'};
  }
  .customTags,
  .react-tagsinput-tag {
    margin-top: 5px;
    color: ${({ theme }) => theme.headingColor};
    background: ${({ theme }) =>
      theme?.dark ? 'rgba(255, 255, 255, 0.12)' : 'var(--bs-tagChips)'};
  }
`;
export default Reportingcreation;